import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { apiService, type CartItemPayload } from '@/services/apiService';
import type { CartBraceletGift } from '@/services/braceletGiftService';

import { ActiveOffer } from '@/types/offer';

export interface CartItem {
  isFreeGift?: boolean;
  id: string; // composite: `${productId}|${variantKey}` for variants, else productId
  productId: string;
  variantKey?: string;
  variantCountry?: string;
  variantGrade?: string;
  variantCarat?: number;
  name: string;
  price: number;
  appPrice?: number;
  originalPrice: number;
  image: string;
  quantity: number;
  category?: string;
  categorySlug?: string;
  productSlug?: string;
  description?: string;

  baseUnitPrice?: number;
  effectiveUnitPrice?: number;
  lineBaseTotal?: number;
  lineDiscount?: number;
  lineFinalTotal?: number;
  hasActiveOffer?: boolean;
  activeOffer?: ActiveOffer;
}

export interface CartContextType {
  cart: CartItem[];
  braceletGift: CartBraceletGift | null;
  isLoadingCart: boolean;
  cartLoadError: string | null;
  addToCart: (item: Omit<CartItem, 'quantity'>, onAuthRequired?: () => void, quantityToAdd?: number) => Promise<boolean>;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  refreshCart: () => Promise<void>;
  cartTotal: number;
  cartCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [braceletGift, setBraceletGift] = useState<CartBraceletGift | null>(null);
  const [isLoadingCart, setIsLoadingCart] = useState<boolean>(true);
  const [cartLoadError, setCartLoadError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  // Load cart from API when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadCartFromAPI();
    } else {
      setCart([]);
      setBraceletGift(null);
      setCartLoadError(null);
      setIsLoadingCart(false);
    }
  }, [isAuthenticated]);

  const loadCartFromAPI = async () => {
    setIsLoadingCart(true);
    setCartLoadError(null);
    try {
      const result = await apiService.getCart();
      if (result.success && result.data) {
        const data = result.data;
        setBraceletGift(Array.isArray(data) ? null : data.braceletGift || null);
        const items = Array.isArray(data) ? data : Array.isArray(data.list) ? data.list : [];
        const mappedCart = items.map((item) => {
          const isFreeGift = item.isFreeGift === true;
          const variantKey = item.variantKey || '';
          const baseUnitPrice = isFreeGift ? 0 : item.baseUnitPrice ?? item.appPrice ?? item.price ?? 0;
          const effectiveUnitPrice =
            isFreeGift ? 0 : item.hasActiveOffer === true && item.effectiveUnitPrice != null
              ? item.effectiveUnitPrice
              : (item.effectivePrice != null ? item.effectivePrice : (item.price ?? baseUnitPrice));

          const hasOffer = item.hasActiveOffer === true || Boolean(item.activeOffer);

          return {
            id: isFreeGift ? `gift:${item.productId}` : variantKey ? `${item.productId}|${variantKey}` : item.productId,
            isFreeGift,
            productId: item.productId,
            variantKey: variantKey || undefined,
            variantCountry: item.variantCountry || undefined,
            variantGrade: item.variantGrade || undefined,
            variantCarat: item.variantCarat != null ? Number(item.variantCarat) : undefined,
            name: item.productName || item.name || '',
            price: effectiveUnitPrice,
            appPrice: baseUnitPrice,
            originalPrice: isFreeGift ? 0 : item.originalPrice ?? baseUnitPrice,
            image: item.imageUrl || item.image || '',
            quantity: item.quantity,
            category: item.category,
            categorySlug: item.categorySlug,
            productSlug: item.productSlug,
            description: item.description,

            baseUnitPrice,
            effectiveUnitPrice,
            lineBaseTotal: isFreeGift ? 0 : item.lineBaseTotal ?? (baseUnitPrice * item.quantity),
            lineDiscount: isFreeGift ? 0 : item.lineDiscount ?? (hasOffer ? Math.max(0, (baseUnitPrice - effectiveUnitPrice) * item.quantity) : 0),
            lineFinalTotal: isFreeGift ? 0 : item.lineFinalTotal ?? (effectiveUnitPrice * item.quantity),
            hasActiveOffer: hasOffer,
            activeOffer: item.activeOffer,
          };
        });
        setCart(mappedCart);
      } else {
        console.error('Failed to load cart:', result.error);
        setCartLoadError(result.error || 'Unable to load your cart. Please try again.');
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      setCartLoadError('Unable to load your cart. Please try again.');
    } finally {
      setIsLoadingCart(false);
    }
  };

  const addToCart = async (item: Omit<CartItem, 'quantity'>, onAuthRequired?: () => void, quantityToAdd: number = 1) => {
    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login to add items to your cart",
      });
      onAuthRequired?.();
      return false;
    }

    const existingItem = cart.find(cartItem => cartItem.id === item.id);
    
    if (existingItem) {
      // Item already in cart - use update API instead of add
      const newQuantity = existingItem.quantity + quantityToAdd;
      const result = await apiService.updateCartItem(existingItem.productId || existingItem.id, newQuantity, existingItem.variantKey);
      
      if (result.success) {
        await loadCartFromAPI();
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to update cart",
          variant: "destructive",
        });
      }
      return result.success;
    }

    // New item - use add API
    const payload: CartItemPayload = {
      productId: item.productId || item.id,
      productName: item.name,
      appPrice: item.price,
      originalPrice: item.originalPrice,
      imageUrl: item.image,
      quantity: quantityToAdd,
      category: item.category || '',
      description: item.description || '',
    };
    if (item.variantKey) {
      payload.variantKey = item.variantKey;
      payload.variantCountry = item.variantCountry || '';
      payload.variantGrade = item.variantGrade || '';
      payload.variantCarat = item.variantCarat || 0;
    }

    const result = await apiService.addToCart(payload);
    
    if (result.success) {
      await loadCartFromAPI();
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to add item to cart",
        variant: "destructive",
      });
    }
    return result.success;
  };

  const removeFromCart = async (id: string) => {
    const target = cart.find(c => c.id === id);
    if (target?.isFreeGift) return;
    const result = await apiService.removeFromCart(target?.productId || id, target?.variantKey);
    
    if (result.success) {
      await loadCartFromAPI();
      toast({
        title: "Removed from cart",
        description: "Item has been removed from your cart",
      });
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to remove item from cart",
        variant: "destructive",
      });
    }
  };

  const updateQuantity = async (id: string, quantity: number) => {
    if (quantity < 1) {
      removeFromCart(id);
      return;
    }
    const target = cart.find(c => c.id === id);
    if (!target || target.isFreeGift) return;
    const result = await apiService.updateCartItem(target.productId || target.id, quantity, target.variantKey);

    if (result.success) {
      await loadCartFromAPI();
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to update quantity",
        variant: "destructive",
      });
    }
  };

  const clearCart = async () => {
    const result = await apiService.clearCart();
    
    if (result.success) {
      setCart([]);
      setBraceletGift(null);
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to clear cart",
        variant: "destructive",
      });
    }
  };

  const cartTotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((count, item) => count + (item.isFreeGift ? 0 : item.quantity), 0);

  return (
    <CartContext.Provider value={{ 
      cart,
      braceletGift,
      isLoadingCart,
      cartLoadError,
      addToCart, 
      removeFromCart, 
      updateQuantity, 
      clearCart, 
      refreshCart: loadCartFromAPI,
      cartTotal, 
      cartCount 
    }}>
      {children}
    </CartContext.Provider>
  );
};

// Default values for when context is not available (prevents crash during initialization)
const defaultCartContext: CartContextType = {
  cart: [],
  braceletGift: null,
  isLoadingCart: false,
  cartLoadError: null,
  addToCart: async () => false,
  removeFromCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  refreshCart: async () => {},
  cartTotal: 0,
  cartCount: 0,
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    console.warn('useCart called outside CartProvider, returning default values');
    return defaultCartContext;
  }
  return context;
};
